import { Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-otp-verification',
  templateUrl: './otp-verification.component.html',
  styleUrls: ['./otp-verification.component.scss']
})
export class OtpVerificationComponent implements OnInit, OnChanges, OnDestroy {

  // Parent-provided FormControl (for combined forms). If absent, component owns its own.
  @Input() control: AbstractControl | null = null;

  // Timer inputs.
  @Input() resendCooldownSeconds: number = 60;

  // FE 3-strike (0 disables the in-component counter).
  @Input() maxAttempts: number = 0;

  // UI mode + state from parent.
  @Input() showVerifyButton: boolean = false;
  @Input() verifyButtonLabel: string = 'Verify';
  @Input() isVerifying: boolean = false;
  @Input() isResending: boolean = false;
  @Input() errorMsg: string = '';
  // Parent bumps this counter to signal a wrong-OTP from server; component decrements.
  @Input() invalidOtpTick: number = 0;
  // Parent bumps this to start/reset the resend cooldown after a successful (re)send.
  @Input() resendStartTick: number = 0;
  // Parent bumps this to fully reset the component on a new cycle (re-login):
  // restores attemptsRemaining, restarts the cooldown, clears cells, realigns ticks.
  @Input() resetTick: number = 0;
  // Atomically disables cells (readonly), Verify button, and Resend hint. Used by login-section's max-attempts state.
  @Input() disabled: boolean = false;

  @Output() verify: EventEmitter<string> = new EventEmitter<string>();
  @Output() resend: EventEmitter<void> = new EventEmitter<void>();
  @Output() maxAttemptsExceeded: EventEmitter<void> = new EventEmitter<void>();

  internalControl: FormControl = new FormControl('', [Validators.required]);
  cellValues: string[] = ['', '', '', '', '', ''];
  otpIndexes: number[] = [0, 1, 2, 3, 4, 5];
  @ViewChildren('otpCell') otpCellElements!: QueryList<ElementRef<HTMLInputElement>>;

  trackByIndex(index: number): number {
    return index;
  }

  countdown: number = -1;
  attemptsRemaining: number = 0;

  private resendTimerId: any = null;
  private lastInvalidTick: number = 0;
  private lastResendTick: number = 0;
  private lastResetTick: number = 0;

  constructor(private ngZone: NgZone) {}

  ngOnInit(): void {
    this.attemptsRemaining = this.maxAttempts;
    if (this.resendCooldownSeconds > 0) {
      this.startResendCooldown();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Full reset for a new MFA cycle (cached instance is reused, ngOnInit won't re-run).
    // Handle FIRST: when resetTick + invalidOtpTick change in the same pass, the realign
    // below must run before the invalid-OTP handler, else a stale tick fires a phantom attempt.
    const isResetting =
      changes['resetTick'] && !changes['resetTick'].firstChange && this.resetTick !== this.lastResetTick;
    if (isResetting) {
      this.lastResetTick = this.resetTick;
      this.attemptsRemaining = this.maxAttempts;
      this.lastInvalidTick = this.invalidOtpTick; // realign so a stale tick isn't re-read as a wrong OTP
      this.clearCells();
      this.startResendCooldown();
    }
    if (!isResetting && changes['invalidOtpTick'] && this.invalidOtpTick !== this.lastInvalidTick) {
      this.lastInvalidTick = this.invalidOtpTick;
      // Clear entered digits on every wrong OTP (matches the User App), independent of the
      // in-component 3-strike counter which onInvalidOtp() early-returns on when disabled.
      this.clearCells();
      this.onInvalidOtp();
    }
    if (changes['resendStartTick'] && this.resendStartTick !== this.lastResendTick) {
      this.lastResendTick = this.resendStartTick;
      this.clearCells();
      this.startResendCooldown();
    }
    if (changes['maxAttempts'] && !changes['maxAttempts'].firstChange) {
      this.attemptsRemaining = this.maxAttempts;
    }
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  get effectiveControl(): AbstractControl {
    return this.control ?? this.internalControl;
  }

  onResendClick(): void {
    if (this.countdown >= 0 || this.isResending || this.disabled) return;
    this.resend.emit();
  }

  private syncControlFromCells(): void {
    const joined = this.cellValues.join('');
    this.effectiveControl.setValue(joined);
    if (joined.length > 0) {
      this.effectiveControl.markAsTouched();
    }
  }

  onCellInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length > 1) {
      value = value.charAt(value.length - 1);
    }
    this.cellValues[index] = value;
    input.value = value;
    this.syncControlFromCells();
  }

  onCellKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Tab') return;

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      this.cellValues[index] = event.key;
      input.value = event.key;
      this.syncControlFromCells();
      if (index < 5) {
        this.focusCell(index + 1);
      }
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.cellValues[index]) {
        this.cellValues[index] = '';
        input.value = '';
        this.syncControlFromCells();
        return;
      }
      if (index > 0) {
        this.cellValues[index - 1] = '';
        this.syncControlFromCells();
        this.focusCell(index - 1);
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      this.cellValues[index] = '';
      input.value = '';
      this.syncControlFromCells();
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusCell(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusCell(index + 1);
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onCellPaste(event: ClipboardEvent, startIndex: number): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, 6);
    if (digits.length === 0) return;

    for (let i = 0; i < 6; i++) {
      const target = startIndex + i;
      if (target > 5) break;
      this.cellValues[target] = digits[i] || '';
    }
    this.syncControlFromCells();
    const nextIndex = Math.min(startIndex + digits.length, 5);
    this.focusCell(nextIndex);
  }

  private focusCell(index: number): void {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      const el = this.otpCellElements?.toArray()[index]?.nativeElement;
      if (el) el.focus();
    });
  }

  private clearCells(): void {
    this.cellValues = ['', '', '', '', '', ''];
    this.effectiveControl.setValue('');
    this.effectiveControl.markAsUntouched();
  }

  onVerifyClick(): void {
    const val = (this.effectiveControl.value ?? '').toString();
    if (val.length !== 6) {
      this.effectiveControl.markAsTouched();
      return;
    }
    this.verify.emit(val);
  }

  startResendCooldown(): void {
    this.clearResendTimer();
    this.countdown = this.resendCooldownSeconds;
    this.resendTimerId = setInterval(() => {
      this.countdown--;
      if (this.countdown < 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private onInvalidOtp(): void {
    if (this.maxAttempts <= 0) return;
    this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);
    if (this.attemptsRemaining === 0) {
      this.maxAttemptsExceeded.emit();
    }
  }

  private clearResendTimer(): void {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
  }
}
